import { type CalendarDate } from "../dateAdapter";
import type { TimeFormat } from "../pickers/BasePicker";
import {
  TIME_FORMAT,
  parseArrayOrValue,
  parseDatesRange,
  parseValue,
} from "./parse";

export const DEFAULT_MINUTE_STEP = 5;

export type ValidationResult =
  | { valid: true; value: string }
  | { valid: false; message: string };

export type CompareUnit = "year" | "month" | "day" | "hour";

export type DateConstraintOptions = {
  dateFormat: string;
  localization?: string;
  minDate?: string | CalendarDate | Date;
  maxDate?: string | CalendarDate | Date;
  disable?: string | string[] | CalendarDate | CalendarDate[] | Date | Date[];
  enable?: string | string[] | CalendarDate | CalendarDate[] | Date | Date[];
  minuteStep?: number;
  /** Compare min/max/disable/enable at this granularity. */
  unit?: CompareUnit;
  /** When true, also enforce minuteStep (defaults to 5). */
  checkMinutes?: boolean;
};

export type RangeConstraintOptions = DateConstraintOptions & {
  allowSameEndDate?: boolean;
  separator?: string;
};

const VALID_EMPTY: ValidationResult = { valid: true, value: "" };

function invalid(message: string): ValidationResult {
  return { valid: false, message };
}

/** Strict parse: value must parse and round-trip through the format. */
export function parseStrict(
  raw: string,
  dateFormat: string,
  localization?: string,
): CalendarDate | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = parseValue(trimmed, dateFormat, localization);
  if (!parsed) {
    return undefined;
  }
  if (parsed.format(dateFormat) !== trimmed) {
    return undefined;
  }
  return parsed;
}

function isBeforeBound(
  date: CalendarDate,
  bound: CalendarDate,
  unit?: CompareUnit,
): boolean {
  return unit ? date.isBefore(bound, unit) : date.isBefore(bound);
}

function isAfterBound(
  date: CalendarDate,
  bound: CalendarDate,
  unit?: CompareUnit,
): boolean {
  return unit ? date.isAfter(bound, unit) : date.isAfter(bound);
}

function checkMinMax(
  date: CalendarDate,
  options: DateConstraintOptions,
): ValidationResult | null {
  const { dateFormat, localization, minDate, maxDate, unit } = options;
  const min = parseValue(minDate, dateFormat, localization);
  if (min && isBeforeBound(date, min, unit)) {
    return invalid("Date is before the minimum");
  }
  const max = parseValue(maxDate, dateFormat, localization);
  if (max && isAfterBound(date, max, unit)) {
    return invalid("Date is after the maximum");
  }
  return null;
}

function checkDisableEnable(
  date: CalendarDate,
  options: DateConstraintOptions,
): ValidationResult | null {
  const { dateFormat, localization, disable, enable, unit = "day" } = options;

  const disabled = parseArrayOrValue(disable, dateFormat, localization);
  if (disabled?.some((d) => date.isSame(d, unit))) {
    return invalid("This date is not available");
  }

  const enabled = parseArrayOrValue(enable, dateFormat, localization);
  if (enabled && !enabled.some((d) => date.isSame(d, unit))) {
    return invalid("This date is not available");
  }

  return null;
}

function checkMinuteStep(
  date: CalendarDate,
  minuteStep: number | undefined,
): ValidationResult | null {
  if (!minuteStep) {
    return null;
  }
  if (date.minute() % minuteStep !== 0) {
    return invalid(`Minutes must be in steps of ${minuteStep}`);
  }
  return null;
}

function applyConstraints(
  date: CalendarDate,
  formatted: string,
  options: DateConstraintOptions,
): ValidationResult {
  const minMax = checkMinMax(date, options);
  if (minMax) {
    return minMax;
  }
  const disableEnable = checkDisableEnable(date, options);
  if (disableEnable) {
    return disableEnable;
  }
  if (options.checkMinutes) {
    const stepResult = checkMinuteStep(date, options.minuteStep);
    if (stepResult) {
      return stepResult;
    }
  }
  return { valid: true, value: formatted };
}

/** Validate a single date/time string against format and constraints. */
export function validateDateValue(
  raw: string,
  options: DateConstraintOptions,
): ValidationResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return VALID_EMPTY;
  }
  const parsed = parseStrict(trimmed, options.dateFormat, options.localization);
  if (!parsed) {
    return invalid("Invalid date");
  }
  return applyConstraints(parsed, parsed.format(options.dateFormat), options);
}

export function validateTimeValue(
  raw: string,
  options: {
    timeFormat?: TimeFormat;
    localization?: string;
    minuteStep?: number;
    disableMinute?: boolean;
  },
): ValidationResult {
  const dateFormat = TIME_FORMAT[options.timeFormat || "24"];
  const trimmed = raw.trim();
  if (!trimmed) {
    return VALID_EMPTY;
  }
  const parsed = parseStrict(trimmed, dateFormat, options.localization);
  if (!parsed) {
    return invalid("Invalid time");
  }
  if (options.disableMinute && parsed.minute() !== 0) {
    return invalid("Minutes must be 0");
  }
  if (!options.disableMinute) {
    const stepResult = checkMinuteStep(parsed, options.minuteStep);
    if (stepResult) {
      return stepResult;
    }
  }
  return { valid: true, value: parsed.format(dateFormat) };
}

export function validateDatesRangeValue(
  raw: string,
  options: RangeConstraintOptions,
): ValidationResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return VALID_EMPTY;
  }

  const separator = options.separator ?? " - ";
  const unit = options.unit ?? "day";
  const { start, end } = parseDatesRange(
    trimmed,
    options.dateFormat,
    separator,
  );

  if (!start || !end) {
    return invalid("Invalid date range");
  }

  const startStr = start.format(options.dateFormat);
  const endStr = end.format(options.dateFormat);
  const expected = `${startStr}${separator}${endStr}`;
  if (expected !== trimmed) {
    return invalid("Invalid date range");
  }

  if (end.isBefore(start, unit)) {
    return invalid("End date must be after the start date");
  }
  if (end.isSame(start, unit) && options.allowSameEndDate === false) {
    return invalid("End date must be after the start date");
  }

  for (const side of [start, end]) {
    const constrained = applyConstraints(
      side,
      side.format(options.dateFormat),
      { ...options, checkMinutes: false },
    );
    if (!constrained.valid) {
      return constrained;
    }
  }

  return { valid: true, value: expected };
}
